// Hubs data access. This is the ONLY place that talks to the `hubs` table.
// Pages call these functions; they never write Supabase queries inline.
//
// The two mapping functions (rowToHub / hubInputToRow) translate between the
// database shape (snake_case rows) and the app shape (camelCase types). They are
// pure and unit-tested in hubsApi.test.ts.

import { supabase } from "../../lib/supabase";
import type { Hub, HubInput } from "../../lib/types";

/** A raw row as it comes back from the `hubs` table. */
interface HubRow {
  id: string;
  account_id: string;
  name: string;
  code: string | null;
  city: string | null;
  state: string | null;
  region: string | null;
  manager: string | null;
  manager_email: string | null;
  regional_manager: string | null;
  lat: number | null;
  lng: number | null;
  capacity: number | null;
  mhe_polybag: number | null;
  mhe_cartons: number | null;
  mhe_irregular: number | null;
  active: boolean;
}

/** DB row -> app Hub. */
export function rowToHub(row: HubRow): Hub {
  return {
    id: row.id,
    name: row.name,
    code: row.code ?? undefined,
    city: row.city ?? undefined,
    state: row.state ?? undefined,
    region: row.region ?? undefined,
    manager: row.manager ?? undefined,
    managerEmail: row.manager_email ?? undefined,
    regionalManager: row.regional_manager ?? undefined,
    lat: row.lat,
    lng: row.lng,
    capacity: row.capacity,
    mhePolybag: row.mhe_polybag,
    mheCartons: row.mhe_cartons,
    mheIrregular: row.mhe_irregular,
    active: row.active,
  };
}

/** App HubInput -> DB row fields (account_id added by the caller's context). */
export function hubInputToRow(accountId: string, input: HubInput) {
  return {
    account_id: accountId,
    name: input.name.trim(),
    code: input.code?.trim() || null,
    city: input.city?.trim() || null,
    state: input.state?.trim() || null,
    region: input.region?.trim() || null,
    manager: input.manager?.trim() || null,
    manager_email: input.managerEmail?.trim() || null,
    regional_manager: input.regionalManager?.trim() || null,
    lat: input.lat ?? null,
    lng: input.lng ?? null,
    capacity: input.capacity ?? null,
    mhe_polybag: input.mhePolybag ?? null,
    mhe_cartons: input.mheCartons ?? null,
    mhe_irregular: input.mheIrregular ?? null,
    active: input.active,
  };
}

/** List all hubs for an account, newest-relevant first (by name). */
export async function listHubs(accountId: string): Promise<Hub[]> {
  const { data, error } = await supabase
    .from("hubs")
    .select("*")
    .eq("account_id", accountId)
    .order("name");
  if (error) throw error;
  return (data as HubRow[]).map(rowToHub);
}

/** Create a new hub and return it. */
export async function createHub(accountId: string, input: HubInput): Promise<Hub> {
  const { data, error } = await supabase
    .from("hubs")
    .insert(hubInputToRow(accountId, input))
    .select("*")
    .single();
  if (error) throw error;
  return rowToHub(data as HubRow);
}

/** Update an existing hub and return it. Scoped by id AND account_id. */
export async function updateHub(accountId: string, id: string, input: HubInput): Promise<Hub> {
  const { data, error } = await supabase
    .from("hubs")
    .update(hubInputToRow(accountId, input))
    .eq("id", id)
    .eq("account_id", accountId)
    .select("*")
    .single();
  if (error) throw error;
  return rowToHub(data as HubRow);
}

/**
 * Set a hub's active flag (our soft-delete / pause). Scoped by id AND account_id.
 * We never hard-delete hubs from the UI.
 */
export async function setHubActive(accountId: string, id: string, active: boolean): Promise<void> {
  const { error } = await supabase
    .from("hubs")
    .update({ active })
    .eq("id", id)
    .eq("account_id", accountId);
  if (error) throw error;
}
